/**
 * Security Kit Cart Synchronization - Enhanced Version
 * Works alongside theme.js without modifications
 * Automatically syncs Security Kit quantity with bracket products
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    securityKitHandle: 'flexi-plate-bracket-security-kit',
    securityKitTitle: 'security kit',
    bracketKeyword: 'bracket',
    updateDelay: 300,
  };

  // State management
  let isProcessing = false;
  let pendingSync = null;

  /**
   * Utility: Debounce function
   */
  function debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  /**
   * Get all cart items from the DOM
   */
  function getCartItems() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return [];
    
    const lineItems = cartDrawer.querySelectorAll('line-item');
    return Array.from(lineItems).map(item => {
      const quantityInput = item.querySelector('.quantity-selector__input');
      const productLink = item.querySelector('.line-item-info a, .line-item-info p');
      const removeLink = item.querySelector('.remvEbtn, [href*="url_to_remove"]');
      const lineKey = quantityInput ? quantityInput.dataset.lineKey : null;
      
      return {
        element: item,
        quantity: quantityInput ? parseInt(quantityInput.value) : 0,
        title: productLink ? productLink.textContent.trim() : '',
        lineKey: lineKey,
        quantityInput: quantityInput,
        removeLink: removeLink
      };
    });
  }

  /**
   * Check if item is a Security Kit
   */
  function isSecurityKit(item) {
    const titleLower = item.title.toLowerCase();
    return titleLower.includes(CONFIG.securityKitTitle);
  }

  /**
   * Check if item is a Bracket (excluding security kit)
   */
  function isBracket(item) {
    const titleLower = item.title.toLowerCase();
    return titleLower.includes(CONFIG.bracketKeyword) && !isSecurityKit(item);
  }

  /**
   * Count total brackets in cart
   */
  function getTotalBracketCount() {
    const items = getCartItems();
    return items.reduce((total, item) => {
      return isBracket(item) ? total + item.quantity : total;
    }, 0);
  }

  /**
   * Find Security Kit in cart
   */
  function findSecurityKit() {
    const items = getCartItems();
    return items.find(item => isSecurityKit(item));
  }

  /**
   * Update cart item quantity via Shopify API
   */
  async function updateCartItemQuantity(lineKey, quantity) {
    if (isProcessing) {
      console.log('[Security Kit] Update already in progress, queuing...');
      return;
    }

    isProcessing = true;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const data = await response.json();
      console.log('[Security Kit] Cart updated successfully');
      
      // Trigger cart refresh
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      return data;
    } catch (error) {
      console.error('[Security Kit] Error updating cart:', error);
      throw error;
    } finally {
      isProcessing = false;
    }
  }

  /**
   * Sync Security Kit quantity to match brackets
   */
  async function syncSecurityKitQuantity() {
    if (isProcessing) {
      console.log('[Security Kit] Sync skipped - update in progress');
      return;
    }

    const bracketCount = getTotalBracketCount();
    const securityKit = findSecurityKit();

    console.log('[Security Kit] Sync check:', {
      bracketCount,
      securityKitExists: !!securityKit,
      securityKitQuantity: securityKit?.quantity
    });

    if (!securityKit || !securityKit.lineKey) {
      console.log('[Security Kit] No security kit found in cart');
      return;
    }

    // Only update if quantities don't match and there are brackets
    if (securityKit.quantity !== bracketCount && bracketCount > 0) {
      console.log(`[Security Kit] Syncing: ${securityKit.quantity} → ${bracketCount}`);
      await updateCartItemQuantity(securityKit.lineKey, bracketCount);
    } else {
      console.log('[Security Kit] Quantities already match or no brackets');
    }
  }

  /**
   * Apply restrictions to Security Kit UI
   */
  function applySecurityKitRestrictions() {
    const bracketCount = getTotalBracketCount();
    const securityKit = findSecurityKit();
    
    if (!securityKit || bracketCount === 0) {
      console.log('[Security Kit] No restrictions needed');
      return;
    }

    console.log('[Security Kit] Applying UI restrictions');
    
    const { element, removeLink } = securityKit;
    
    // Keep remove button enabled - users can remove the Security Kit if they want
    if (removeLink) {
      // Make sure it's fully functional
      removeLink.style.pointerEvents = 'auto';
      removeLink.style.opacity = '1';
      removeLink.style.cursor = 'pointer';
      removeLink.title = 'Remove Security Kit';
    }
    
    // Disable quantity controls (but keep remove button enabled)
    const quantitySelector = element.querySelector('quantity-selector');
    if (quantitySelector) {
      const buttons = quantitySelector.querySelectorAll('.quantity-selector__button');
      const input = quantitySelector.querySelector('.quantity-selector__input');
      
      buttons.forEach(btn => {
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.4';
        btn.style.cursor = 'not-allowed';
        btn.disabled = true;
      });
      
      if (input) {
        input.readOnly = true;
        input.style.opacity = '0.7';
        input.style.cursor = 'not-allowed';
      }
    }
    
    // Add visual indicator
    addVisualIndicator(element, bracketCount);
  }

  /**
   * Add visual indicator to Security Kit
   */
  function addVisualIndicator(lineItemElement, bracketCount) {
    // Remove existing indicator
    const existingNote = lineItemElement.querySelector('.security-kit-sync-note');
    if (existingNote) {
      existingNote.remove();
    }
    
    const note = document.createElement('div');
    note.className = 'security-kit-sync-note';
    note.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="vertical-align: middle; margin-right: 6px;">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
        <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      Qty auto-synced with ${bracketCount} bracket${bracketCount !== 1 ? 's' : ''}
    `;
    note.style.cssText = `
      font-size: 11px;
      color: #666;
      background: #f8f8f8;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      padding: 8px 10px;
      margin-top: 8px;
      display: flex;
      align-items: center;
      line-height: 1.4;
    `;
    
    const infoSection = lineItemElement.querySelector('.line-item-info > .v-stack');
    if (infoSection) {
      infoSection.appendChild(note);
    }
  }

  /**
   * Show notification
   */
  function showNotification(message) {
    let notification = document.getElementById('security-kit-notification');
    
    if (!notification) {
      notification = document.createElement('div');
      notification.id = 'security-kit-notification';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ff4444;
        color: white;
        padding: 16px 20px;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        font-size: 14px;
        font-weight: 500;
        max-width: 320px;
        z-index: 999999;
        display: none;
        animation: slideIn 0.3s ease-out;
      `;
      document.body.appendChild(notification);
    }
    
    notification.textContent = message;
    notification.style.display = 'block';
    notification.style.animation = 'slideIn 0.3s ease-out';
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => {
        notification.style.display = 'none';
      }, 300);
    }, 3500);
  }

  /**
   * Add CSS animations
   */
  function addStyles() {
    if (document.getElementById('security-kit-sync-styles')) return;

    const style = document.createElement('style');
    style.id = 'security-kit-sync-styles';
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Initialize the sync mechanism
   */
  function initialize() {
    console.log('[Security Kit] Initializing cart sync...');
    addStyles();
    applySecurityKitRestrictions();
  }

  /**
   * Handle cart change events
   */
  const debouncedSync = debounce(async () => {
    await syncSecurityKitQuantity();
    // Re-apply restrictions after sync
    setTimeout(applySecurityKitRestrictions, 500);
  }, CONFIG.updateDelay);

  /**
   * Listen for cart changes from theme.js
   */
  function setupEventListeners() {
    // Listen for the cart:change event that theme.js fires
    document.addEventListener('cart:change', (event) => {
      console.log('[Security Kit] Cart change detected:', event.detail);
      
      // Wait a bit for the cart to update, then sync
      setTimeout(() => {
        debouncedSync();
      }, 300);
    });

    // Listen for cart refresh
    document.addEventListener('cart:refresh', () => {
      console.log('[Security Kit] Cart refresh detected');
      setTimeout(initialize, 300);
    });

    // Listen for line item changes specifically
    document.addEventListener('line-item:change', (event) => {
      console.log('[Security Kit] Line item changed:', event.detail);
      debouncedSync();
    });
  }

  /**
   * Start everything
   */
  function start() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initialize();
        setupEventListeners();
      });
    } else {
      initialize();
      setupEventListeners();
    }

    // Watch for cart drawer to appear/update
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length) {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && !cartDrawer.hasAttribute('data-security-kit-initialized')) {
            cartDrawer.setAttribute('data-security-kit-initialized', 'true');
            console.log('[Security Kit] Cart drawer detected, initializing...');
            setTimeout(initialize, 200);
          }
        }
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    console.log('[Security Kit] Cart sync system ready');
  }

  // Start the system
  start();

})();